import { useCallback, useEffect, useMemo, useState } from 'react';
import { UnifiedTable } from '../../../../components/UnifiedTable';
import { bulkOzonAutomations, getOzonAutomationsTable } from '../../../../services/ozonBidder';
import { fmtDateTime } from '../../../../utils/ruDates';

const MODE_LABELS = { AUTO: 'Автоматический', CONTROLLED: 'С подтверждением', OBSERVE: 'Наблюдение' };
const MODES = Object.entries(MODE_LABELS);
const LINK_STYLE = {
  background: 'none', border: 'none', padding: 0, color: '#2563eb', cursor: 'pointer',
  textAlign: 'left', font: 'inherit', fontWeight: 600, whiteSpace: 'normal',
};

const number = value => (value == null ? '—' : Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 0 }));

function Change({ value }) {
  if (value == null) return <span className='oz-auto__muted'>—</span>;
  const sign = value > 0 ? '+' : '';
  const color = value > 0 ? '#166534' : value < 0 ? '#b91c1c' : '#64748b';
  return <span style={{ color, fontWeight: 600 }}>{sign}{Number(value).toFixed(1)}%</span>;
}

const failureText = err => (typeof err?.detail === 'string' ? err.detail
  : err?.detail?.message || err?.message || 'Не удалось выполнить');

// The same goal inputs the wizard asks for, entered once for every selected group.
function StrategyInputs({ spec, objective, onChange }) {
  const required = spec?.required_objective || [];
  if (!required.length && spec?.key !== 'TURNOVER_CAP') return null;
  return <>
    {required.includes('target_value') && <label>Целевое значение
      <input type='number' value={objective.target_value ?? ''}
        onChange={e => onChange({ target_value: e.target.value === '' ? null : Number(e.target.value) })} /></label>}
    {required.includes('target_date') && <label>К какой дате
      <input type='date' value={objective.target_date ?? ''}
        onChange={e => onChange({ target_date: e.target.value })} /></label>}
    {spec?.key === 'TURNOVER_CAP' && <label>Период, дней
      <input type='number' value={objective.period_days ?? 30}
        onChange={e => onChange({ period_days: Number(e.target.value) })} /></label>}
  </>;
}

// Массовое изменение настроек: пустое поле и «не менять» оставляют настройку группы как есть.
const BULK_PERMISSIONS = [['bid', 'Ставка'], ['price', 'Цена'], ['budget', 'Бюджет РК'], ['jev', 'Курс ведёт Jev']];
const BULK_NUMBERS = [
  ['max_bid', 'Максимальная ставка, ₽'], ['bid_change_limit_pct', 'Макс. шаг ставки, %'],
  ['price_change_limit_pct', 'Макс. шаг цены, %'], ['target_drr', 'Предельный общий ДРР за день, %'],
  ['target_margin_pct', 'Целевая рентабельность, %'], ['min_margin_pct', 'Минимальная рентабельность, %'],
  ['experiment_budget', 'Бюджет проверок, ₽'],
];

export function buildSettingsChange(flags, numbers, rollback) {
  const permissions = {};
  Object.entries(flags).forEach(([key, value]) => { if (value === 'on' || value === 'off') permissions[key] = value === 'on'; });
  const constraints = {};
  Object.entries(numbers).forEach(([key, value]) => {
    if (value === '' || value == null) return;
    constraints[key] = key === 'target_drr' ? Math.round(Number(value) * 1e4) / 1e6 : Number(value);
  });
  if (rollback === 'on' || rollback === 'off') constraints.auto_rollback = rollback === 'on';
  return { permissions, constraints };
}

function SettingsPanel({ count, busy, onApply, onCancel }) {
  const [flags, setFlags] = useState({});
  const [numbers, setNumbers] = useState({});
  const [rollback, setRollback] = useState('');
  const change = buildSettingsChange(flags, numbers, rollback);
  const empty = !Object.keys(change.permissions).length && !Object.keys(change.constraints).length;
  return <div className='oz-auto__bulk oz-auto__bulk--settings'>
    <div className='oz-auto__fields'>
      {BULK_PERMISSIONS.map(([key, label]) => <label key={key}>{label}
        <select value={flags[key] || ''} onChange={e => setFlags(current => ({ ...current, [key]: e.target.value }))}>
          <option value=''>не менять</option><option value='on'>включить</option><option value='off'>выключить</option>
        </select></label>)}
      {BULK_NUMBERS.map(([key, label]) => <label key={key}>{label}
        <input type='number' min='0' placeholder='не менять' value={numbers[key] ?? ''}
          onChange={e => setNumbers(current => ({ ...current, [key]: e.target.value }))} /></label>)}
      <label>Возвращать изменение при ухудшении
        <select value={rollback} onChange={e => setRollback(e.target.value)}>
          <option value=''>не менять</option><option value='on'>да</option><option value='off'>нет</option>
        </select></label>
    </div>
    <button type='button' className='oz-auto__primary' disabled={busy || empty} onClick={() => onApply(change)}>
      Применить к {count}</button>
    <button type='button' disabled={busy} onClick={onCancel}>Отмена</button>
  </div>;
}

export default function OzonAutomationsTable({ accountId, request, strategies, onOpen, onChanged }) {
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [panel, setPanel] = useState(null);  // 'mode' | 'strategy' | 'archive' | 'purge'
  // The archive keeps groups that changed something, for a month, so their
  // journal can still be read; from here they come back or go for good.
  const [archived, setArchived] = useState(false);
  const [retention, setRetention] = useState(30);
  const [mode, setMode] = useState('CONTROLLED');
  const [strategyType, setStrategyType] = useState('');
  const [objective, setObjective] = useState({});

  const load = useCallback(async () => {
    try {
      const data = await getOzonAutomationsTable({ request, accountId, archived });
      setRows(data.items || []);
      if (data.retention_days) setRetention(data.retention_days);
    } catch (err) { setMessage({ kind: 'error', text: failureText(err) }); setRows([]); }
  }, [request, accountId, archived]);
  useEffect(() => { load(); }, [load]);

  const spec = useMemo(() => strategies.find(item => item.key === strategyType), [strategies, strategyType]);
  const selectedNames = (rows || []).filter(row => selected.includes(row.id)).map(row => row.name);

  const run = async (action, extra = {}) => {
    setBusy(true); setMessage(null);
    try {
      const result = await bulkOzonAutomations({ request, accountId, ids: selected, action, ...extra });
      const parts = [];
      if (action === 'archive') {
        if (result.archived?.length) parts.push(`в архив: ${result.archived.length}`);
        if (result.deleted?.length) parts.push(`удалено без следа: ${result.deleted.length}`);
      } else if (action === 'restore') {
        parts.push(`восстановлено ${result.done?.length || 0} — на паузе, включите, когда нужно`);
      } else if (action === 'delete') {
        parts.push(`удалено навсегда: ${result.deleted?.length || 0}`);
      } else {
        parts.push(`изменено ${result.done?.length || 0} из ${selected.length}`);
      }
      const failed = result.failed || [];
      const warnings = result.warnings || [];
      setMessage({
        kind: failed.length || warnings.length ? 'error' : 'ok',
        text: parts.join(', ') + (failed.length
          ? `. Не изменились: ${failed.map(item => `${item.name} — ${item.error}`).join('; ')}` : '.')
          + (warnings.length ? ` Внимание: ${warnings.map(item => `${item.name} — ${item.warning}`).join('; ')}` : ''),
      });
      setPanel(null);
      setSelected([]);
      await load();
      if (onChanged) await onChanged();
    } catch (err) { setMessage({ kind: 'error', text: failureText(err) }); }
    finally { setBusy(false); }
  };

  const columns = [
    { key: 'name', header: 'Группа автоматизации', width: 280, sticky: true,
      render: (value, row) => <button type='button' style={LINK_STYLE}
        onClick={e => { e.stopPropagation(); onOpen(row.id); }}>{value}</button> },
    { key: 'sku_count', header: 'Артикулов', type: 'number', width: 100, align: 'right' },
    { key: 'strategy_title', header: 'Стратегия', width: 190 },
    { key: 'mode_label', header: 'Режим', width: 170 },
    { key: 'orders_7d', header: 'Заказы, 7 дн.', type: 'number', width: 120, align: 'right', format: number },
    { key: 'orders_change_pct', header: 'Динамика заказов', type: 'number', width: 140, align: 'right',
      render: value => <Change value={value} /> },
    { key: 'profit_7d', header: 'Прибыль, 7 дн., ₽', type: 'number', width: 140, align: 'right',
      render: (value, row) => (value == null
        ? <span className='oz-auto__muted' title={row.profit_missing_cogs?.length
          ? 'Не заполнена себестоимость в «Экономике»' : 'Нет данных'}>
          {row.profit_missing_cogs?.length ? 'нет с/с' : '—'}</span>
        : number(value)) },
    { key: 'profit_change_pct', header: 'Динамика прибыли', type: 'number', width: 140, align: 'right',
      render: value => <Change value={value} /> },
  ];

  if (archived) {
    columns.splice(3, 1,
      { key: 'archived_at', header: 'Удалена', width: 150,
        render: value => (value ? fmtDateTime(value) : '—') },
      { key: 'purge_in_days', header: 'До удаления навсегда', type: 'number', width: 170, align: 'right',
        render: value => (value == null ? '—' : `${value} дн.`) });
  }

  const switchView = value => { setArchived(value); setSelected([]); setPanel(null); setMessage(null); setRows(null); };

  const data = (rows || []).map(row => ({
    ...row,
    mode_label: row.status === 'PAUSED' ? 'Пауза'
      : row.status === 'DRAFT' ? 'Черновик' : (MODE_LABELS[row.execution_mode] || row.execution_mode),
  }));

  const viewSwitch = <div className='oz-auto__tabs' role='tablist'>
    {[[false, 'Активные'], [true, 'Архив']].map(([value, label]) => <button type='button' key={label}
      role='tab' aria-selected={archived === value} className={archived === value ? 'is-active' : ''}
      onClick={() => switchView(value)}>{label}</button>)}
  </div>;

  if (rows == null) return <>{viewSwitch}<p>Загрузка таблицы…</p></>;
  if (archived) return <div className='oz-auto__products-tab'>
    {viewSwitch}
    <p className='oz-auto__muted'>Здесь удалённые группы, которые успели что-то изменить в OZON. Журнал
      хранится {retention} дней, потом группа удаляется сама. Восстановленная группа возвращается на паузе.</p>
    <div className='oz-auto__bulk'>
      <strong>Выбрано: {selected.length}</strong>
      <button type='button' disabled={busy || !selected.length} onClick={() => run('restore')}>Восстановить</button>
      <button type='button' disabled={busy || !selected.length} onClick={() => setPanel('purge')}>Удалить навсегда</button>
    </div>
    {panel === 'purge' && <div className='oz-auto__bulk'>
      <span>Удалить {selected.length} групп навсегда вместе с журналом? Вернуть будет нельзя.</span>
      <button type='button' className='oz-auto__primary' disabled={busy} onClick={() => run('delete')}>
        {busy ? 'Удаляем…' : 'Да, удалить навсегда'}</button>
      <button type='button' disabled={busy} onClick={() => setPanel(null)}>Отмена</button>
    </div>}
    {message && <p className={message.kind === 'error' ? 'bidder__error' : 'oz-auto__ok'} role='status'>{message.text}</p>}
    {!data.length ? <p>Архив пуст.</p> : <UnifiedTable
      tableId='ozon-automations-archive'
      columns={columns}
      data={data}
      getRowKey={row => row.id}
      search={search}
      onSearchChange={setSearch}
      selectable
      selectedRows={selected}
      onSelectionChange={setSelected}
      pageSizes={[50, 100, 'all']}
    />}
  </div>;
  return <div className='oz-auto__products-tab'>
    {viewSwitch}
    <div className='oz-auto__bulk'>
      <strong>Выбрано: {selected.length}</strong>
      <button type='button' disabled={busy || !selected.length} onClick={() => setPanel('archive')}>Удалить</button>
      <button type='button' disabled={busy || !selected.length} onClick={() => run('pause')}>Приостановить</button>
      <button type='button' disabled={busy || !selected.length} onClick={() => run('resume')}>Возобновить</button>
      {strategies.length > 1 && <button type='button' disabled={busy || !selected.length} onClick={() => setPanel('strategy')}>Изменить стратегию</button>}
      <button type='button' disabled={busy || !selected.length} onClick={() => setPanel('settings')}>Изменить настройки</button>
      <button type='button' disabled={busy || !rows?.length} onClick={() => setSelected((rows || []).map(row => row.id))}>Выбрать все</button>
      <button type='button' disabled={busy || !selected.length} onClick={() => setSelected([])}>Снять выбор</button>
      <button type='button' disabled={busy || !selected.length} onClick={() => setPanel('mode')}>Изменить режим</button>
    </div>

    {panel === 'archive' && <div className='oz-auto__bulk'>
      <span>Удалить {selected.length} групп? Группы, которые уже меняли ставки или цены, уйдут в архив
        на {retention} дней вместе с журналом; группы без единого изменения удалятся сразу. Перед удалением
        группа вернёт то, что сделала сама: калибровка — исходные ставки и цены, остатки — РК на паузе
        и убранные из РК товары.</span>
      <button type='button' className='oz-auto__primary' disabled={busy} onClick={() => run('archive')}>
        {busy ? 'Удаляем…' : 'Да, удалить'}</button>
      <button type='button' disabled={busy} onClick={() => setPanel(null)}>Отмена</button>
    </div>}

    {panel === 'mode' && <div className='oz-auto__bulk'>
      <label>Режим <select value={mode} onChange={e => setMode(e.target.value)}>
        {MODES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      <button type='button' className='oz-auto__primary' disabled={busy} onClick={() => run('mode', { mode })}>
        Применить к {selected.length}</button>
      <button type='button' disabled={busy} onClick={() => setPanel(null)}>Отмена</button>
    </div>}

    {panel === 'settings' && <SettingsPanel count={selected.length} busy={busy} onCancel={() => setPanel(null)}
      onApply={change => run('settings', change)} />}

    {panel === 'strategy' && <div className='oz-auto__bulk'>
      <label>Стратегия <select value={strategyType}
        onChange={e => { setStrategyType(e.target.value); setObjective({}); }}>
        <option value=''>— выберите —</option>
        {strategies.map(item => <option key={item.key} value={item.key}>{item.title}</option>)}</select></label>
      <StrategyInputs spec={spec} objective={objective}
        onChange={patch => setObjective(current => ({ ...current, ...patch }))} />
      <button type='button' className='oz-auto__primary' disabled={busy || !strategyType}
        onClick={() => run('strategy', { strategyType, objective })}>Применить к {selected.length}</button>
      <button type='button' disabled={busy} onClick={() => setPanel(null)}>Отмена</button>
    </div>}

    {message && <p className={message.kind === 'error' ? 'bidder__error' : 'oz-auto__ok'} role='status'>{message.text}</p>}
    {panel && selectedNames.length > 0 && <p className='oz-auto__muted'>
      {selectedNames.slice(0, 8).join(', ')}{selectedNames.length > 8 ? ` и ещё ${selectedNames.length - 8}` : ''}</p>}

    <UnifiedTable
      tableId='ozon-bidder-automations'
      columns={columns}
      data={data}
      getRowKey={row => row.id}
      search={search}
      onSearchChange={setSearch}
      selectable
      selectedRows={selected}
      onSelectionChange={setSelected}
      pageSizes={[50, 100, 250, 'all']}
      exportFilename='bidder-avtomatizacii'
      onExportCsv
      onCopyTable
      loading={busy}
    />
    <p className='oz-auto__muted'>Динамика — последние 7 полных дней против предыдущих 7. Прибыль — из «Экономики»;
      у группы с незаполненной себестоимостью она не считается. Проверка при смене режима или стратегии —
      та же, что при сохранении настроек: группы, которые не подходят, не изменятся, и будет видно почему.</p>
  </div>;
}
